import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
  lastModified?: string;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://public.readdy.ai/ai/img_res/d72a3498-c157-4cc6-8081-f37fa1e12754.png',
  noIndex = false,
  jsonLd,
  lastModified,
}: SeoHeadProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ailanshe.com';

  useEffect(() => {
    // Title
    document.title = title;

    // Helper: set or create meta
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.match(/\[([^\]]+)="([^"]+)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);
    if (noIndex) {
      setMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    } else {
      setMeta('meta[name="robots"]', 'content', 'index, follow');
    }

    // Canonical
    const canonicalUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
    let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      document.head.appendChild(linkEl);
    }
    linkEl.setAttribute('href', canonicalUrl);

    // OG tags
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[property="og:image"]', 'content', ogImage);

    // Twitter tags
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);

    // last-modified
    const today = lastModified || new Date().toISOString().split('T')[0];
    setMeta('meta[name="last-modified"]', 'content', today);

    // author
    setMeta('meta[name="author"]', 'content', '藍舍AI地產');

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-page="true"]');
    existingScripts.forEach((s) => s.remove());

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-page', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      // Cleanup page-level JSON-LD on unmount
      document.querySelectorAll('script[data-seo-page="true"]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonical, ogImage, noIndex, jsonLd, siteUrl, lastModified]);

  return null;
}
